import { Tabs } from '@opallabs/satellite'
import { NormalizedApproval } from '@opallabs/wormhole/types'
import * as sharedStyles from '../Shared/styles'
import { LocalStorageKeys } from '../types'
import { useSelectedSubTab } from '../useSelectedSubTab'
import { CompletedPanel } from './SubPanels/CompletedPanel'
import { PendingPanel } from './SubPanels/PendingPanel'
import { ApprovalPanelProps } from './types'
import { useCheckpointCreators } from './useCheckpointCreators'

export const ApprovalPanel: React.FC<ApprovalPanelProps> = ({
  approvals,
  completedApprovalsLoading,
  createCheckpoint,
  getCompletedApprovals,
  getOverdueApprovals,
  getPendingApprovals,
  overdueApprovalsLoading,
  pendingApprovalsLoading,
  setApprovalPreviewCard,
  status,
  theme = 'light',
}) => {
  const {
    createCompletedCheckpoint,
    createOverdueCheckpoint,
    createPendingCheckpoint,
  } = useCheckpointCreators({
    approvals,
    completedApprovalsLoading,
    createCheckpoint,
    overdueApprovalsLoading,
    pendingApprovalsLoading,
  })

  const { onTabChange, selectedIndex } = useSelectedSubTab({
    localStorageKey: LocalStorageKeys.approvalsSubTab,
    orderedTabs: ['pending', 'completed'],
  })

  const tabHasUnseen = (subTab: 'pending' | 'completed' | 'overdue'): boolean =>
    status !== null && status.approvals[subTab].unseen > 0

  return (
    <Tabs
      onSelect={onTabChange}
      selectedIndex={selectedIndex}
      styleOverrides={{ tabsWrapper: sharedStyles.subTabWrapper }}
    >
      <Tabs.List
        styleOverrides={{ tabList: sharedStyles.subTabList({ theme }) }}
      >
        <Tabs.Tab
          key="tab-approval-pending"
          styleOverrides={{
            baseTab: sharedStyles.subTab({
              hasUnseen: tabHasUnseen('pending') || tabHasUnseen('overdue'),
              theme,
            }),
            selectedTab: sharedStyles.subTabSelected({ theme }),
          }}
        >
          Pending
        </Tabs.Tab>
        <Tabs.Tab
          key="tab-approval-completed"
          styleOverrides={{
            baseTab: sharedStyles.subTab({
              hasUnseen: tabHasUnseen('completed'),
              theme,
            }),
            selectedTab: sharedStyles.subTabSelected({ theme }),
          }}
        >
          Completed
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel styleOverrides={{ tabPanel: sharedStyles.subTabPanel }}>
        <PendingPanel
          createOverdueCheckpoint={createOverdueCheckpoint}
          createPendingCheckpoint={createPendingCheckpoint}
          getOverdueApprovals={getOverdueApprovals}
          getPendingApprovals={getPendingApprovals}
          isLoading={pendingApprovalsLoading || overdueApprovalsLoading}
          overdueApprovals={approvals.overdue}
          pendingApprovals={approvals.pending}
          setApprovalPreviewCard={(approval: NormalizedApproval | null): void =>
            setApprovalPreviewCard(approval)
          }
          theme={theme}
        />
      </Tabs.Panel>
      <Tabs.Panel styleOverrides={{ tabPanel: sharedStyles.subTabPanel }}>
        <CompletedPanel
          completedApprovals={approvals.completed}
          createCheckpoint={createCompletedCheckpoint}
          getCompletedApprovals={getCompletedApprovals}
          isLoading={completedApprovalsLoading}
          setApprovalPreviewCard={(approval: NormalizedApproval | null): void =>
            setApprovalPreviewCard(approval)
          }
          theme={theme}
        />
      </Tabs.Panel>
    </Tabs>
  )
}
